import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getCaseSheet,
  type CaseHistoryEntry,
  type CaseSheetClinicalView,
  type CaseSheetReadViewAny,
} from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { doctorLensHref } from '@/dashboard/doctor/doctor-patient-lens-view';
import { AcBadge, AcButton, EmptyState, SectionShell } from '@/dashboard/ui';
import { CASE_SHEET_LABELS } from '@/dashboard/clinical-workbench/case-sheet-labels';
import {
  formatWholeInr,
  planItemSummary,
} from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import {
  consultErrorMessage,
  rupeeGlyphToRs,
  type CaseHistoryLoadState,
} from '@/dashboard/doctor/doctor-consult-view';

/**
 * DOCTOR-CLINICAL-WORKSPACE-V1 -- the CASE HISTORY RAIL (R-3 clinical
 * flavor; tenant walkthrough parity: "case sheet entries accumulate into
 * CASE HISTORY"). DOCTOR-OS-EXPERIENCE-V1 (C8) retitles it "Same case
 * sheet - earlier visits" -- ONE longitudinal chart presentation -- and
 * LIFTS the fetch to the consult page so the rail and the treatment ledger
 * render from the SAME read (one fetch, two renders).
 *
 * This patient's past visits, chronological (newest first), FINALIZED
 * content only, doctor-fenced (the server read is doctor-only + org/clinic
 * fenced; visits at other org clinics surface as an honest count, never
 * content). An entry expands into the READ-ONLY finalized sheet fetched
 * through the EXISTING role-safe GET /api/case-sheets/:id. Empty states are
 * designed: no linked patient -> the identity seam; linked with no priors ->
 * "First visit on record".
 */

type OpenSheetState =
  | { kind: 'closed' }
  | { kind: 'loading'; caseSheetId: number }
  | { kind: 'error'; caseSheetId: number; text: string }
  | { kind: 'open'; caseSheetId: number; sheet: CaseSheetClinicalView };

/** Read-only rendering of one finalized past sheet (clinical audience). */
function ReadOnlySheet({ sheet }: { sheet: CaseSheetClinicalView }): JSX.Element {
  return (
    <div className="history-rail__sheet" data-testid={`history-sheet-${sheet.id}`}>
      <dl className="cockpit-notes__summary">
        <div>
          <dt>{CASE_SHEET_LABELS.chiefComplaint}</dt>
          <dd>{sheet.chiefComplaint ?? 'Not recorded'}</dd>
        </div>
        <div>
          <dt>{CASE_SHEET_LABELS.findingsDiagnosis}</dt>
          <dd>{sheet.findingsDiagnosis ?? 'Not recorded'}</dd>
        </div>
        <div>
          <dt>{CASE_SHEET_LABELS.clinicalNote}</dt>
          <dd>{sheet.clinicalNote ?? 'Not recorded'}</dd>
        </div>
      </dl>
      {sheet.planItems.length > 0 ? (
        <ul className="history-rail__plan">
          {sheet.planItems.map((item) => (
            <li key={item.id}>{rupeeGlyphToRs(planItemSummary(item))}</li>
          ))}
        </ul>
      ) : null}
      <p className="clinic-workbench__muted">
        Finalized - read only. Recorded by {sheet.authoredByRole}.
      </p>
    </div>
  );
}

export interface CaseHistoryRailProps {
  /** The linked golden-record patient id, or null (identity seam). */
  readonly patientId: string | null;
  /** The visit currently open in the workspace (excluded from the rail). */
  readonly currentAppointmentId: number;
  /** The page-lifted case-history read (shared with the treatment ledger;
   *  DOCTOR-OS-EXPERIENCE-V1 C8: one fetch, two renders). */
  readonly history: CaseHistoryLoadState;
}

export default function CaseHistoryRail({
  patientId,
  currentAppointmentId,
  history,
}: CaseHistoryRailProps): JSX.Element {
  const [openSheet, setOpenSheet] = useState<OpenSheetState>({ kind: 'closed' });
  // DOCTOR-OS-BUILD-PHASE-1-V1 (brief s.7.10): the rail is the COMPACT
  // previous-visits glance -- last three visits; the full timeline is one
  // click deeper through the doctor lens (door 4). The door renders ONLY
  // when the lens is lawfully reachable (the #294 server-computed signal) --
  // never a dead door (LES-016); without it the rail keeps rendering every
  // served entry so no visit becomes unreachable.
  const meCtx = useDashboardMeContext();
  const lensEnabled = meCtx?.me?.doctorLensEnabled === true;
  // BUILD 3 (L-09): the rail lives INSIDE the consult workspace, so the record
  // it opens returns to that consult -- not to a patient search the doctor
  // never visited. THE ONE href builder stamps the origin; no second constant.
  const fullHistoryHref =
    lensEnabled && patientId !== null
      ? currentAppointmentId !== null && currentAppointmentId !== undefined
        ? `${doctorLensHref(patientId, 'consult')}&fromId=${currentAppointmentId}`
        : doctorLensHref(patientId, 'consult')
      : null;

  // The lifted read, narrowed to this rail's view: the CURRENT visit is
  // excluded (it is the workspace itself, one column to the left).
  const state:
    | { kind: 'loading' }
    | { kind: 'error'; text: string }
    | {
        kind: 'ready';
        entries: readonly CaseHistoryEntry[];
        crossClinicCount: number;
      } =
    history.kind === 'ready'
      ? {
          kind: 'ready',
          entries: history.history.entries.filter(
            (e) => e.appointmentId !== currentAppointmentId,
          ),
          crossClinicCount: history.history.crossClinicCount,
        }
      : history;

  const onToggleEntry = useCallback(
    (entry: CaseHistoryEntry) => {
      if (openSheet.kind === 'open' && openSheet.caseSheetId === entry.caseSheetId) {
        setOpenSheet({ kind: 'closed' });
        return;
      }
      setOpenSheet({ kind: 'loading', caseSheetId: entry.caseSheetId });
      getCaseSheet(entry.caseSheetId)
        .then((sheet: CaseSheetReadViewAny) => {
          // The doctor-only rail always receives the clinical view; a
          // different audience shape means the session changed -- fail
          // honestly rather than render a partial sheet.
          if (sheet.audience === 'clinical') {
            setOpenSheet({
              kind: 'open',
              caseSheetId: entry.caseSheetId,
              sheet,
            });
          } else {
            setOpenSheet({
              kind: 'error',
              caseSheetId: entry.caseSheetId,
              text: 'This record is not available for your current role.',
            });
          }
        })
        .catch((err: unknown) =>
          setOpenSheet({
            kind: 'error',
            caseSheetId: entry.caseSheetId,
            text: consultErrorMessage(err).text,
          }),
        );
    },
    [openSheet],
  );

  if (patientId === null) {
    return (
      <SectionShell
        eyebrow="Case history"
        title="Earlier visits"
        testId="history-rail"
      >
        <EmptyState
          title="No patient record linked yet."
          body="Past visits appear here once this lead is linked to a patient record."
          testId="history-rail-unlinked"
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell
      eyebrow="Case history"
      title="Earlier visits"
      testId="history-rail"
    >
      {state.kind === 'loading' ? (
        <p className="day-board__loading" role="status">
          Loading past visits...
        </p>
      ) : state.kind === 'error' ? (
        <p className="day-board__error" role="alert">
          {state.text}
        </p>
      ) : state.entries.length === 0 ? (
        <EmptyState
          title="First visit on record."
          body="Finalized visits accumulate here as this patient's case history."
          testId="history-rail-empty"
        />
      ) : (
        <ul className="history-rail__list" data-testid="history-rail-list">
          {(fullHistoryHref !== null
            ? state.entries.slice(0, 3)
            : state.entries
          ).map((entry) => {
            const isOpen =
              openSheet.kind !== 'closed' &&
              openSheet.caseSheetId === entry.caseSheetId;
            return (
              <li
                key={entry.caseSheetId}
                className="history-rail__entry"
                data-testid={`history-entry-${entry.caseSheetId}`}
              >
                <div className="history-rail__entry-head">
                  <span className="history-rail__date">
                    {formatTimestampIst(entry.createdAt)}
                  </span>
                  {entry.diagnosisCategory !== null ? (
                    <AcBadge tone="neutral">{entry.diagnosisCategory}</AcBadge>
                  ) : null}
                </div>
                <p className="history-rail__complaint">
                  {entry.chiefComplaint ??
                    entry.chiefComplaintCategory ??
                    'No complaint recorded'}
                </p>
                <p className="history-rail__meta">
                  {entry.planItemCount === 0
                    ? 'No procedures'
                    : `${entry.planItemCount} procedure${entry.planItemCount === 1 ? '' : 's'} - ${rupeeGlyphToRs(formatWholeInr(entry.estimatedTotal))}`}
                </p>
                <AcButton
                  size="sm"
                  variant="ghost"
                  onClick={() => onToggleEntry(entry)}
                  data-testid={`history-open-${entry.caseSheetId}`}
                >
                  {isOpen ? 'Close' : 'Open visit record'}
                </AcButton>
                {openSheet.kind === 'loading' &&
                openSheet.caseSheetId === entry.caseSheetId ? (
                  <p className="day-board__loading" role="status">
                    Opening...
                  </p>
                ) : null}
                {openSheet.kind === 'error' &&
                openSheet.caseSheetId === entry.caseSheetId ? (
                  <p className="day-board__error" role="alert">
                    {openSheet.text}
                  </p>
                ) : null}
                {openSheet.kind === 'open' &&
                openSheet.caseSheetId === entry.caseSheetId ? (
                  <ReadOnlySheet sheet={openSheet.sheet} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {state.kind === 'ready' && fullHistoryHref !== null ? (
        <p className="history-rail__full" data-testid="history-rail-full-door">
          <Link
            to={fullHistoryHref}
            className="ac-btn ac-btn--ghost ac-btn--sm"
          >
            Open full history
          </Link>
        </p>
      ) : null}
      {state.kind === 'ready' && state.crossClinicCount > 0 ? (
        <p
          className="clinic-workbench__muted"
          data-testid="history-cross-clinic-note"
        >
          {state.crossClinicCount} more visit
          {state.crossClinicCount === 1 ? '' : 's'} recorded at other clinics
          in your organization (outside your clinic scope).
        </p>
      ) : null}
    </SectionShell>
  );
}
